import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Text } from '@/components/shared/element/text';
import { CustomSelectBox } from '@/components/shared/form/custom-select';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { styled } from '@/stitches.config';
import { ITracking } from '@/types/tracking';

const SwitchToggleStyled = styled(Box, {
  outline: 'none',
});

const StyledSwitchBody = styled(Flex, {
  position: 'relative',
  borderRadius: '$rounded-2',
  clipPath: 'inset(0% round 8px)',
  boxShadow: 'inset 0px 0px 0px 2px #fff',
  '&::before': {
    content: "''",
    position: 'absolute',
    display: 'block',
    top: '0',
    left: '0',
    width: '50%',
    height: '$size-full',
    pe: 'none',
    transition: 'transform 0.2s ease',
    transform: 'translateX(0)',
    background: '$white',
  },
  '&.slided': {
    '&::before': {
      transform: 'translateX(100%)',
    },
  },
});

const SwitchRadioInputStyled = styled('input', {
  display: 'none',
});

const SwitchLabelStyled = styled(Text, {
  cursor: 'pointer',
  transform: 'translateZ(0)',
  variants: {
    selected: {
      true: {
        pointerEvents: 'none',
      },
    },
    spacing: {
      sm: {
        py: '$space-2',
        px: '$space-4',
      },
      md: {
        py: '$space-3',
        px: '$space-4',
      },
    },
  },
  defaultVariants: {
    spacing: 'md',
  },
});

const SwitchStyled = styled(CustomSelectBox, {
  minWidth: '$size-60',
  variants: {
    type: {
      mojito: {
        padding: '$space-1',
        backgroundColor: 'rgba(119, 119, 119, 0.5)',
        '@md': {
          width: 'max-content',
          mx: 'auto',
        },
        [`${StyledSwitchBody}`]: {
          borderRadius: 'none',
          clipPath: 'none',
          boxShadow: '$shadow-none',
          '&::before': {
            borderRadius: '$rounded-1',
            boxShadow: '$shadow-100',
            background: 'linear-gradient(180deg, #E0FF63 0%, $lime 100%)',
          },
          '&.slided': {
            '&::before': {
              background: 'linear-gradient(180deg, $white 0%, $gray400 100%)',
            },
          },
        },
        [`${SwitchToggleStyled}`]: {
          '@md': {
            minWidth: '$size-56',
          },
        },
      },
      gapBorder: {
        [`${StyledSwitchBody}`]: {
          boxShadow: 'inset 0px 0px 0px 1px rgba(255, 255, 255, 0.6)',
          padding: '$space-2',
          '&::before': {
            top: '$space-2',
            left: '$space-2',
            width: 'calc(50% - 8px)',
            height: 'calc(100% - 16px)',
            borderRadius: '$rounded-1',
          },
          '@xl': {
            boxShadow: 'inset 0px 0px 0px 2px rgba(255, 255, 255, 0.6)',
          },
        },
      },
    },
    size: {
      sm: {},
      md: {},
    },
    single: {
      true: {
        pointerEvents: 'none !important',
        [`${StyledSwitchBody}`]: {
          boxShadow: '$shadow-none',
          '&::before': {
            width: '100% !important',
            background: '#262626',
          },
        },
      },
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export interface SwitchOption {
  label: string;
  value: string;
  textColor?: string;
}

export interface SwitchProps extends React.ComponentProps<typeof SwitchStyled> {
  id?: string;
  name: string;
  options: SwitchOption[];
  value: string;
  dataTest?: string;
  dataTracking?: ITracking;
  onSwitch?: (selectedValue: string, index: number, item: SwitchOption) => void;
}

const Switch = ({
  id,
  name,
  onSwitch,
  dataTracking,
  value,
  dataTest = 'model',
  options = [],
  type,
  size,
  css,
}: SwitchProps) => {
  const [selected, setSelected] = useState<string>(value);
  const selectedIndex = useMemo(
    () => options.findIndex((item) => item.value === selected),
    [options, selected]
  );

  const onSwitchChange = useCallback(
    (value: string, index: number, item: SwitchOption) => {
      if (options.length === 1) return;
      if (value !== selected) {
        setSelected(value);
        onSwitch?.(value, index, item);
      }
    },
    [options, selected, onSwitch]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (value !== selected) {
        setSelected(value);
      }
    }, 0);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <SwitchStyled
      id={id}
      rounded="2"
      test={{
        dataTest: `${dataTest}_switch`,
      }}
      type={type}
      single={options.length === 1}
      css={css}
    >
      <StyledSwitchBody
        alignItems="center"
        className={selectedIndex !== 0 ? 'slided' : ''}
      >
        {options.map((item, index) => {
          const isActive = item.value === selected;
          return (
            <SwitchToggleStyled
              key={index}
              component="button"
              position="relative"
              width="full"
              border="none"
              backgroundColor="transparent"
              textAlign="center"
              title={item.label}
              test={{
                dataTest: `${dataTest}_toggle_switch`,
              }}
              tracking={{
                dataTrack: dataTracking?.dataTrack as string,
                dataTrackSection: dataTracking?.dataTrackSection,
                dataTrackValue: `${
                  dataTracking?.dataTrackValue
                    ? dataTracking?.dataTrackValue + '-'
                    : ''
                }${item?.value}_switch`,
                dataTrackText: `${item?.label}`,
              }}
            >
              <SwitchRadioInputStyled
                type="radio"
                className={index === 0 ? 'left' : 'right'}
                name={name}
                id={`${item.value}-${name}`}
                value={item.value}
                onChange={() => onSwitchChange(item?.value, index, item)}
                checked={isActive}
                aria-checked={isActive}
              />
              <SwitchLabelStyled
                htmlFor={`${item.value}-${name}`}
                as="label"
                spacing={size}
                size={
                  type === 'mojito'
                    ? 'text-h3'
                    : {
                        '@initial': 'text-body-2',
                        '@lg': size === 'sm' ? 'text-base-2' : 'text-base',
                      }
                }
                font={
                  type === 'mojito'
                    ? undefined
                    : type === 'gapBorder'
                      ? isActive
                        ? 'bold'
                        : 'base'
                      : 'bold'
                }
                variant={
                  options.length === 1
                    ? 'text-white'
                    : type === 'mojito'
                      ? isActive
                        ? 'text-gray-darkest'
                        : 'text-disabled'
                      : isActive
                        ? 'text-gray-darkest'
                        : 'text-white'
                }
                wrap={type === 'mojito'}
                noWrap={!type}
                data-test={`${dataTest}_switch_label`}
                selected={isActive}
                css={{
                  px: type === 'mojito' ? '$space-0' : undefined,
                  py: type === 'mojito' ? '$space-1' : undefined,
                }}
              >
                {item.label}
              </SwitchLabelStyled>
            </SwitchToggleStyled>
          );
        })}
      </StyledSwitchBody>
    </SwitchStyled>
  );
};

Switch.displayName = 'Switch';
export default memo(Switch);
